// AC-BIN-009: 提出の一部が通らなかった時のエラーバナーと「詳細を見る」 のダイアログが読む、
// 通らなかった札ごとの理由の区分と文言。 件数の数え方は `summarizeIntradaySubmitResults`
// (`intradaySubmitViolation.ts`) が決め、 ここは 1 札の失敗を区分に分けて文言にするだけ。
import { Code, ConnectError } from '@connectrpc/connect'
import type { TFunction } from 'i18next'

import {
  isIntradayRetryLaterError,
  translateIntradayServerError,
} from '~/models/intradayServerError'
import type { IntradayBidRow } from '~/pages/IntradayBidding/models/intradayBidRow'

/**
 * 通らなかった札の理由の区分。
 * - `conflict`: 照合 (AC-BIN-123。 画面に読み込んだ後に更新されていた) で通らなかった。 バナーの {M}
 * - `gcPassed`: 確認の後にその札のコマの GC を過ぎていた。 照合にも当たる時もこちら (バナーの {K})
 * - `other`: それ以外。 札ごとの理由は AC-BIN-058 の区分の文言 (バナーの {K})
 */
export type IntradaySubmitFailureKind = 'conflict' | 'gcPassed' | 'other'

/** 「詳細を見る」 の 1 行に並べる札の項目 (コマ・エリア・発注主体区分・売/買・入札価格・入札量)。 */
export type IntradaySubmitFailureBid = Pick<
  IntradayBidRow,
  | 'id'
  | 'timeCode'
  | 'areaCode'
  | 'direction'
  | 'priceYenPerKwh'
  | 'volumeMw'
  | 'orderMethod'
  | 'issuerType'
  | 'withdrawAt'
>

export type IntradaySubmitFailure = {
  bid: IntradaySubmitFailureBid
  kind: IntradaySubmitFailureKind
  /** `other` の文言を AC-BIN-058 の区分で引くための元のエラー。 */
  error: unknown
}

/**
 * RPC の拒否を区分に分ける。 GC を過ぎていた札は、 照合 (Aborted) にも当たる時も `gcPassed`
 * (AC-BIN-009)。 GC の判定は呼び出し側が結果の時点で行う (backend は提出・承認申請の GC を
 * 専用の理由では返さないため、 札のコマの GC と現在時刻で決める)。
 */
export const classifyIntradaySubmitFailure = (
  error: unknown,
  isGcPassed: boolean,
): IntradaySubmitFailureKind => {
  if (isGcPassed) return 'gcPassed'
  if (error instanceof ConnectError && error.code === Code.Aborted) {
    return 'conflict'
  }
  return 'other'
}

/** 札ごとの理由の文言 (AC-BIN-009)。 */
export const intradaySubmitFailureReason = (
  failure: IntradaySubmitFailure,
  t: TFunction,
): string => {
  switch (failure.kind) {
    case 'conflict':
      return t('page:biddingIntraday.submit.failure.reasonConflict')
    case 'gcPassed':
      return t('page:biddingIntraday.bidCreate.gcExceeded')
    case 'other':
      return translateIntradayServerError(failure.error, t)
  }
}

/**
 * 時間をおけば通る失敗か (ui-message-guide「部品の選び方」 の「時間をおけば通るもの」)。
 * GC 超過・照合の札は作り直し・画面の更新が要るので当たらない。 それ以外は
 * `isIntradayRetryLaterError` (エラー文言を決める `translateIntradayServerError` と同じ分け方) で決める。
 */
export const isRetryLaterFailure = (failure: IntradaySubmitFailure): boolean =>
  failure.kind === 'other' && isIntradayRetryLaterError(failure.error)
